import { Info, Plus, Trash2 } from 'lucide-react'
import {
  Controller,
  useFieldArray,
  type Control,
  type FieldErrors,
  type UseFormRegister,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { FormField } from '@/components/forms/FormField'
import { Button, IconButton } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Textarea } from '@/components/ui/Textarea'
import { tAny } from '@/i18n'
import { BLOCK_META, HIGHLIGHT_ICON_LABELS } from '../../blocks'
import type { HomepageDraft } from '../../blocks'
import { HIGHLIGHT_ICONS, type BlockType } from '../../types'
import { PictureUpload } from './PictureUpload'

interface FieldsProps {
  index: number
  type: BlockType
  control: Control<HomepageDraft>
  register: UseFormRegister<HomepageDraft>
  errors: FieldErrors<HomepageDraft>['blocks']
}

type BlockErrors = NonNullable<NonNullable<FieldsProps['errors']>[number]>

/** The fields of one block, by type. */
export function BlockFields({ index, type, control, register, errors }: FieldsProps) {
  const { t } = useTranslation('homepage')
  const e: BlockErrors | undefined = errors?.[index]
  const p = `blocks.${index}` as const

  return (
    <div className="grid grid-cols-1 gap-4">
      <FormField
        label={t('Title')}
        error={e?.title?.message}
        hint={t('Leave empty to use the default title.')}
      >
        <Input
          {...register(`${p}.title`)}
          placeholder={tAny(BLOCK_META[type].defaultTitle)}
          maxLength={120}
        />
      </FormField>

      {type === 'text' ? (
        <FormField label={t('Text')} error={e?.body?.message} required>
          <Textarea {...register(`${p}.body`)} rows={7} maxLength={5000} />
        </FormField>
      ) : null}

      {type === 'list' ? (
        <>
          <FormField label={t('Introduction')} error={e?.body?.message}>
            <Textarea {...register(`${p}.body`)} rows={2} maxLength={5000} />
          </FormField>
          <StringList index={index} control={control} error={e?.items?.message ?? e?.items?.root?.message} />
        </>
      ) : null}

      {type === 'highlights' ? (
        <HighlightsFields index={index} control={control} register={register} errors={e} />
      ) : null}
      {type === 'faq' ? <FaqFields index={index} control={control} register={register} errors={e} /> : null}
      {type === 'gallery' ? (
        <GalleryFields index={index} control={control} register={register} errors={e} />
      ) : null}

      {type === 'programmes' || type === 'admissions' ? (
        <>
          <FormField label={t('Introduction')} error={e?.body?.message}>
            <Textarea {...register(`${p}.body`)} rows={3} maxLength={5000} />
          </FormField>
          <AutoNote>
            {type === 'programmes' ? (
              <>
                {t('The classes and séries come from')}{' '}
                <Link to="/admin/academic/classes" className="font-medium text-brand-700 hover:underline">
                  {t('Classes & sections')}
                </Link>
                .
              </>
            ) : (
              t(
                'Whether applications are open, the dates and the “Apply” button come from the online admissions settings. Without online admissions, visitors do not see this block.',
              )
            )}
          </AutoNote>
        </>
      ) : null}

      {type === 'announcements' ? (
        <>
          <Controller
            control={control}
            name={`${p}.limit`}
            render={({ field }) => (
              <FormField label={t('Number of news items')} error={e?.limit?.message}>
                <Select
                  value={field.value ?? 3}
                  onChange={(ev) => field.onChange(Number(ev.target.value))}
                  options={[1, 2, 3, 4, 5, 6].map((n) => ({ value: n, label: String(n) }))}
                />
              </FormField>
            )}
          />
          <AutoNote>
            {t(
              'Shows the latest published announcements addressed to everyone with “Show on the public homepage” ticked.',
            )}
          </AutoNote>
        </>
      ) : null}

      {type === 'contact' ? (
        <>
          <FormField label={t('Note')} error={e?.body?.message}>
            <Textarea {...register(`${p}.body`)} rows={2} maxLength={5000} />
          </FormField>
          <FormField
            label={t('Map link')}
            error={e?.mapUrl?.message}
            hint={t('Optional: a link to the school on a map (OpenStreetMap, Google Maps…).')}
          >
            <Input
              {...register(`${p}.mapUrl`)}
              type="url"
              inputMode="url"
              placeholder="https://"
              maxLength={500}
            />
          </FormField>
          <AutoNote>
            {t('The address, phone, email and opening hours come from the')}{' '}
            <Link to="/admin/settings/school" className="font-medium text-brand-700 hover:underline">
              {t('school profile')}
            </Link>
            .
          </AutoNote>
        </>
      ) : null}
    </div>
  )
}

function AutoNote({ children }: { children: React.ReactNode }) {
  return (
    <p className="flex items-start gap-2 rounded-control bg-surface-sunken p-3 text-xs text-ink-soft">
      <Info className="mt-0.5 size-3.5 shrink-0 text-brand-600" aria-hidden="true" />
      <span>{children}</span>
    </p>
  )
}

function StringList({
  index,
  control,
  error,
}: {
  index: number
  control: Control<HomepageDraft>
  error?: string
}) {
  const { t } = useTranslation('homepage')
  return (
    <Controller
      control={control}
      name={`blocks.${index}.items`}
      render={({ field, fieldState }) => {
        const items = field.value
        const set = (next: string[]) => field.onChange(next)
        return (
          <fieldset className="min-w-0 space-y-2">
            <legend className="mb-1.5 text-[0.8125rem] font-medium text-ink-soft">{t('Items')}</legend>
            {items.map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  aria-label={t('Item {{n}}', { n: i + 1 })}
                  value={item}
                  maxLength={200}
                  onChange={(ev) => set(items.map((v, j) => (j === i ? ev.target.value : v)))}
                  aria-invalid={Boolean(fieldState.error?.[i as never]) || undefined}
                />
                <IconButton
                  label={t('Remove item {{n}}', { n: i + 1 })}
                  icon={<Trash2 />}
                  onClick={() => set(items.filter((_, j) => j !== i))}
                />
              </div>
            ))}
            {error ? (
              <p className="text-xs font-medium text-danger-600" role="alert">
                {tAny(error)}
              </p>
            ) : null}
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<Plus />}
              disabled={items.length >= 30}
              onClick={() => set([...items, ''])}
            >
              {t('Add an item')}
            </Button>
          </fieldset>
        )
      }}
    />
  )
}

interface NestedProps {
  index: number
  control: Control<HomepageDraft>
  register: UseFormRegister<HomepageDraft>
  errors: BlockErrors | undefined
}

function HighlightsFields({ index, control, register, errors }: NestedProps) {
  const { t } = useTranslation('homepage')
  const list = useFieldArray({ control, name: `blocks.${index}.highlights` })
  const listError = errors?.highlights?.message ?? errors?.highlights?.root?.message
  return (
    <fieldset className="min-w-0 space-y-3">
      <legend className="mb-1.5 text-[0.8125rem] font-medium text-ink-soft">{t('Highlights')}</legend>
      {list.fields.map((f, i) => (
        <div
          key={f.id}
          className="grid grid-cols-1 gap-3 rounded-control border border-line p-3 sm:grid-cols-[10rem_1fr]"
        >
          <Controller
            control={control}
            name={`blocks.${index}.highlights.${i}.icon`}
            render={({ field }) => (
              <FormField label={t('Icon')}>
                <Select
                  value={field.value}
                  onChange={(ev) => field.onChange(ev.target.value)}
                  options={HIGHLIGHT_ICONS.map((k) => ({ value: k, label: tAny(HIGHLIGHT_ICON_LABELS[k]) }))}
                />
              </FormField>
            )}
          />
          <FormField label={t('Title')} error={errors?.highlights?.[i]?.title?.message} required>
            <Input {...register(`blocks.${index}.highlights.${i}.title`)} maxLength={80} />
          </FormField>
          <FormField
            label={t('Text')}
            error={errors?.highlights?.[i]?.text?.message}
            className="sm:col-span-2"
          >
            <Textarea {...register(`blocks.${index}.highlights.${i}.text`)} rows={2} maxLength={300} />
          </FormField>
          <div className="sm:col-span-2">
            <Button variant="ghost" size="sm" leftIcon={<Trash2 />} onClick={() => list.remove(i)}>
              {t('Remove this highlight')}
            </Button>
          </div>
        </div>
      ))}
      {listError ? (
        <p className="text-xs font-medium text-danger-600" role="alert">
          {tAny(listError)}
        </p>
      ) : null}
      <Button
        variant="ghost"
        size="sm"
        leftIcon={<Plus />}
        disabled={list.fields.length >= 6}
        onClick={() => list.append({ title: '', text: '', icon: 'star' })}
      >
        {t('Add a highlight')}
      </Button>
    </fieldset>
  )
}

function FaqFields({ index, control, register, errors }: NestedProps) {
  const { t } = useTranslation('homepage')
  const list = useFieldArray({ control, name: `blocks.${index}.faq` })
  const listError = errors?.faq?.message ?? errors?.faq?.root?.message
  return (
    <fieldset className="min-w-0 space-y-3">
      <legend className="mb-1.5 text-[0.8125rem] font-medium text-ink-soft">
        {t('Questions and answers')}
      </legend>
      {list.fields.map((f, i) => (
        <div key={f.id} className="grid grid-cols-1 gap-3 rounded-control border border-line p-3">
          <FormField
            label={t('Question {{n}}', { n: i + 1 })}
            error={errors?.faq?.[i]?.question?.message}
            required
          >
            <Input {...register(`blocks.${index}.faq.${i}.question`)} maxLength={200} />
          </FormField>
          <FormField label={t('Answer')} error={errors?.faq?.[i]?.answer?.message} required>
            <Textarea {...register(`blocks.${index}.faq.${i}.answer`)} rows={3} maxLength={2000} />
          </FormField>
          <div>
            <Button variant="ghost" size="sm" leftIcon={<Trash2 />} onClick={() => list.remove(i)}>
              {t('Remove this question')}
            </Button>
          </div>
        </div>
      ))}
      {listError ? (
        <p className="text-xs font-medium text-danger-600" role="alert">
          {tAny(listError)}
        </p>
      ) : null}
      <Button
        variant="ghost"
        size="sm"
        leftIcon={<Plus />}
        disabled={list.fields.length >= 30}
        onClick={() => list.append({ question: '', answer: '' })}
      >
        {t('Add a question')}
      </Button>
    </fieldset>
  )
}

function GalleryFields({ index, control, register, errors }: NestedProps) {
  const { t } = useTranslation('homepage')
  const list = useFieldArray({ control, name: `blocks.${index}.images` })
  const listError = errors?.images?.message ?? errors?.images?.root?.message
  return (
    <fieldset className="min-w-0 space-y-3">
      <legend className="mb-1.5 text-[0.8125rem] font-medium text-ink-soft">{t('Pictures')}</legend>
      <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {list.fields.map((f, i) => (
          <li key={f.id} className="min-w-0 overflow-hidden rounded-control border border-line">
            {f.url ? <img src={f.url} alt="" className="aspect-[4/3] w-full object-cover" /> : null}
            <div className="space-y-2 p-3">
              <FormField
                label={t('Caption')}
                error={errors?.images?.[i]?.caption?.message ?? errors?.images?.[i]?.fileId?.message}
              >
                <Input {...register(`blocks.${index}.images.${i}.caption`)} maxLength={150} />
              </FormField>
              <Button variant="ghost" size="sm" leftIcon={<Trash2 />} onClick={() => list.remove(i)}>
                {t('Remove this picture')}
              </Button>
            </div>
          </li>
        ))}
      </ul>
      {listError ? (
        <p className="text-xs font-medium text-danger-600" role="alert">
          {tAny(listError)}
        </p>
      ) : null}
      <PictureUpload
        label={t('Add a picture')}
        disabled={list.fields.length >= 12}
        onUploaded={(img) => list.append({ ...img, caption: '' })}
      />
    </fieldset>
  )
}
